import _ from "lodash";
/**--external-- */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

/**--internal-- */
import {
  getFilterPropFromStateMemoized,
  getDependentFilters,
  getTabFilterConfigMemoized,
} from "../modules/Utils";
import { useI18n } from "Hooks";

/**--relative-- */
import DynamicFiltersWrapper from "./DynamicFiltersWrapper";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import { getFiltersDisplayTextMemoized } from "./DynamicFiltersWrapper/utils";
import classes from "./Filters.scss";

const Filters = props => {
  const {
    initialAppliedFilters,
    updateFilterObject,
    updateFilterStatus,
    activeTab,
    curriculumProgramId,
    curriculumType,
    updateFilterItemsLocaleForActiveTab,
    plannerElementSets,
    isProjectNode,
    noFiltersApplicable,
    projectNodeDetails,
    testId,
  } = props;

  const { t } = useI18n(["common", "snp"]);

  const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters);
  const [tabWiseSearchText, setTabWiseSearchText] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [loadingFilters, setLoadingFilters] = useState([]);
  const [parentFilterObj, setParentFilterObj] = useState({});

  const searchText = _.get(tabWiseSearchText, activeTab, "");

  const activeTabAppliedFilters = _.get(appliedFilters, activeTab, {});

  const activeTabFilterOptions = _.get(filterOptions, `${activeTab}`, {});

  const activeTabAcademicYearFilterValue = _.get(
    appliedFilters,
    `${activeTab}.ACADEMIC_YEAR[0]`,
    ""
  );

  const activeTabClassesFilterOptions = _.get(
    filterOptions,
    `${activeTab}.COURSE`,
    []
  );

  const currentTabFiltersConfig = getTabFilterConfigMemoized({
    academicYearId: activeTabAcademicYearFilterValue,
    curriculumProgramId,
  });

  const areFiltersApplicable =
    _.size(
      _.get(
        currentTabFiltersConfig,
        `${activeTab}.curriculumWiseAvailableFilters.${curriculumType}`
      )
    ) > 0 && !noFiltersApplicable;

  const isSearchApplicable = _.includes(
    _.get(
      currentTabFiltersConfig,
      `${activeTab}.searchAvailableInCurriculums`,
      []
    ),
    curriculumType
  );

  const areDependentFiltersReady = _.isEmpty(parentFilterObj);

  const areAllFiltersReady =
    !areFiltersApplicable ||
    (_.isEmpty(loadingFilters) &&
      !_.isEmpty(activeTabFilterOptions) &&
      areDependentFiltersReady);

  const showFilters = isSearchApplicable || areFiltersApplicable;

  const updateFilters = ({ appliedFilters, searchText }) => {
    const filterPropFromState = getFilterPropFromStateMemoized({
      appliedFilters,
      activeTab,
      curriculumType,
      // Parakh: can have different prop name
      isProjectNode,
    });

    const filtersDisplayText = getFiltersDisplayTextMemoized({
      appliedFilters,
      allFiltersOptions: activeTabFilterOptions,
      activeTab,
      t,
      curriculumType,
      plannerElementSets,
      currentTabFiltersConfig,
    });

    const filterObjWithSearch = { ...filterPropFromState, searchText };

    updateFilterObject({ newFilterObj: filterObjWithSearch });

    updateFilterItemsLocaleForActiveTab({ data: filtersDisplayText });
  };

  useEffect(() => {
    if (!areAllFiltersReady) {
      updateFilterStatus({ loaded: false });
      return;
    }

    updateFilters({ appliedFilters: activeTabAppliedFilters, searchText });

    updateFilterStatus({ loaded: true });
  }, [areAllFiltersReady, activeTab]);

  useEffect(() => {
    if (!areAllFiltersReady) {
      return;
    }

    updateFilters({ appliedFilters: activeTabAppliedFilters, searchText });
  }, [activeTabAppliedFilters, searchText, areAllFiltersReady]);

  useEffect(() => {
    if (!areFiltersApplicable) {
      updateAppliedFilters({ newAppliedFilters: {} });
    }
  }, [areFiltersApplicable, activeTab]);

  useEffect(() => {
    /* Remove dependent filters from parentFilterObj that no longer exist when switching between 1.0 and 2.0 setups via the AY filter */

    if (!_.isEmpty(parentFilterObj)) {
      const { parent, dependentFilters: oldDependentFilters } = parentFilterObj;

      const newDependentFilters = getDependentFilters({
        parent,
        activeTab,
        curriculumType,
        currentTabFiltersConfig,
      });

      const removedDependentFilters = _.difference(
        oldDependentFilters,
        newDependentFilters
      );

      _.forEach(removedDependentFilters, filter => {
        updateAppliedFilters({
          appliedFilters: { [filter]: [] },
        });
        markDependentFilterAsUpdated({ filter });
      });
    }
  }, [currentTabFiltersConfig]);

  const updateTabWiseSearchText = useCallback(
    value => {
      setTabWiseSearchText(previousState => {
        return {
          ...previousState,
          [activeTab]: value,
        };
      });
    },
    [activeTab]
  );

  const updateSearchText = useMemo(
    () =>
      _.debounce(value => {
        updateTabWiseSearchText(value);
      }, 300),

    [updateTabWiseSearchText]
  );

  const updateAppliedFilters = useCallback(
    ({ appliedFilters }) => {
      setAppliedFilters(prev => {
        return {
          ...prev,
          [activeTab]: {
            ...prev[activeTab],
            ...appliedFilters,
          },
        };
      });
    },
    [activeTab]
  );

  const updateLoadingFilters = ({ isReady, filter }) => {
    if (!isReady) {
      updateFilterStatus({ loaded: false });

      setLoadingFilters(loadingFilters => [...loadingFilters, filter]);
    } else {
      setLoadingFilters(loadingFilters =>
        _.filter(loadingFilters, loadingFilter => filter !== loadingFilter)
      );
    }
  };

  const updateFilterOptions = ({
    filterOptions: updatedFilterOptions,
    filter,
  }) => {
    setFilterOptions(filterOptions => {
      return {
        ...filterOptions,
        [activeTab]: {
          ...filterOptions[activeTab],
          [filter]: updatedFilterOptions,
        },
      };
    });
  };

  const updateParentFilter = ({ parent }) => {
    const dependentFilters = getDependentFilters({
      parent,
      activeTab,
      curriculumType,
      currentTabFiltersConfig,
    });

    setParentFilterObj({ parent, dependentFilters });
  };

  const markDependentFilterAsUpdated = ({ filter }) => {
    setParentFilterObj(parentFilterObj => {
      const { parent, dependentFilters } = parentFilterObj;
      const updatedDependentFilters = _.filter(
        dependentFilters,
        dependentFilter => dependentFilter !== filter
      );
      if (_.isEmpty(updatedDependentFilters)) {
        return {};
      }
      return { parent, dependentFilters: updatedDependentFilters };
    });
  };

  return showFilters ? (
    <div className={classes.filter}>
      <DynamicFiltersWrapper
        activeTab={activeTab}
        appliedFilters={activeTabAppliedFilters}
        curriculumType={curriculumType}
        searchText={searchText}
        updateSearchText={updateSearchText}
        updateAppliedFilters={({ appliedFilters }) => {
          updateAppliedFilters({ appliedFilters });
        }}
        updateFilterStatus={updateFilterStatus}
        curriculumProgramId={curriculumProgramId}
        areFiltersApplicable={areFiltersApplicable}
        isSearchApplicable={isSearchApplicable}
        parentFilter={parent}
        updateFilterOptions={updateFilterOptions}
        markDependentFilterAsUpdated={markDependentFilterAsUpdated}
        updateParentFilter={updateParentFilter}
        updateLoadingFilters={updateLoadingFilters}
        parentFilterObj={parentFilterObj}
        plannerElementSets={plannerElementSets}
        updateFilterObject={updateFilterObject}
        isProjectNode={isProjectNode}
        projectNodeDetails={projectNodeDetails}
        currentTabFiltersConfig={currentTabFiltersConfig}
        activeTabClassesFilterOptions={activeTabClassesFilterOptions}
        testId={testId}
      />
    </div>
  ) : null;
};

const mapStateToProps = state => {
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    {}
  );
  return { plannerElementSets };
};

export default connect(mapStateToProps)(Filters);

Filters.defaultProps = {
  initialAppliedFilters: {},
  updateFilterObject: () => {},
  updateFilterStatus: () => {},
  activeTab: "",
  curriculumProgramId: "",
  curriculumType: CURRICULUM_TYPE_PYP,
};

Filters.propTypes = {
  initialAppliedFilters: PropTypes.object,
  updateFilterObject: PropTypes.func,
  updateFilterStatus: PropTypes.func,
  activeTab: PropTypes.string,
  curriculumProgramId: PropTypes.string,
  curriculumType: PropTypes.string,
};
